import type { TooltipProps } from 'recharts';
import { cn } from '@/lib/utils';

interface ChartTooltipProps extends TooltipProps<number, string> {
  /** One row per dataKey, listed in this order; the swatch is a background class shown only when there are several rows. */
  rows: Array<{ key: string; label: string; swatch: string }>;
  format: (value: number) => string;
}

// Figma's chart hover card (Dashboard Ticket/Message › Single line graph): a value pill, or a swatch list for several series.
export function ChartTooltip({ active, payload, label, rows, format }: ChartTooltipProps) {
  const shown = rows.flatMap((row) => {
    const value = payload?.find((item) => item.dataKey === row.key)?.value;
    return typeof value === 'number' ? [{ ...row, value }] : [];
  });
  if (!active) return null;
  // A bridged day has no value of its own; say so instead of showing nothing.
  if (shown.length === 0) {
    return <div className='rounded-[10px] border border-gray-300 bg-white/80 px-1.5 py-1 text-xs leading-4 text-gray-900 shadow-[0_2px_8px_rgba(0,0,0,0.12)] backdrop-blur-sm'>No data · {label}</div>;
  }
  const several = shown.length > 1;
  return (
    <div className={cn('rounded-[10px] border border-gray-300 bg-white/80 shadow-[0_2px_8px_rgba(0,0,0,0.12)] backdrop-blur-sm', several ? 'flex flex-col gap-3 p-3' : 'px-1.5 py-1')}>
      {shown.map((row) => (
        <div key={row.key} className={cn('flex items-center whitespace-nowrap', several ? 'gap-3' : 'gap-1.5')}>
          {several ? <span className={cn('h-4 w-[5px] rounded-full', row.swatch)} /> : null}
          <span className='text-[13px] font-medium leading-4 tabular-nums text-text-emphasis'>{format(row.value)}</span>
          <span className='text-xs leading-4 text-gray-900'>{row.label}</span>
        </div>
      ))}
    </div>
  );
}
